import { apiGet, apiPatch, apiPost } from "./client";

export type GlAccount = {
  id: string;
  code: string;
  name: string;
  type: string;
  isSystem?: boolean;
  deactivatedAt?: string | null;
};

export function fetchProfitLoss(companyRef: string) {
  return apiGet<{
    income: Array<{
      accountId: string;
      code: string;
      name: string;
      amount: string;
    }>;
    expense: Array<{
      accountId: string;
      code: string;
      name: string;
      amount: string;
    }>;
    totalIncome: string;
    totalExpense: string;
    netIncome: string;
    currencyCode: string;
  }>(`/c/${companyRef}/gl/profit-loss`);
}

export function fetchTrialBalance(companyRef: string) {
  return apiGet<{
    lines: Array<{
      accountId: string;
      code: string;
      name: string;
      type: string;
      debit: string;
      credit: string;
      balance: string;
    }>;
    totalDebit: string;
    totalCredit: string;
    balanced: boolean;
    currencyCode: string;
  }>(`/c/${companyRef}/gl/trial-balance`);
}

export function listAccounts(
  companyRef: string,
  opts?: { includeDeactivated?: boolean },
) {
  const q = opts?.includeDeactivated ? "?all=1" : "";
  return apiGet<{ accounts: GlAccount[] }>(
    `/c/${companyRef}/gl/accounts${q}`,
  );
}

export function createAccount(
  companyRef: string,
  body: {
    code: string;
    name: string;
    type: "asset" | "liability" | "equity" | "income" | "expense";
  },
) {
  return apiPost<{ account: GlAccount }>(
    `/c/${companyRef}/gl/accounts`,
    body,
  );
}

export function updateAccount(
  companyRef: string,
  accountId: string,
  body: { name?: string; reactivate?: boolean },
) {
  return apiPatch<{ account: GlAccount }>(
    `/c/${companyRef}/gl/accounts/${accountId}`,
    body,
  );
}

export function deactivateAccount(companyRef: string, accountId: string) {
  return apiPost<{ account: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/gl/accounts/${accountId}/deactivate`,
  );
}

export function fetchAccountLedger(
  companyRef: string,
  accountId: string,
  opts?: { limit?: number },
) {
  const q =
    opts?.limit != null ? `?limit=${encodeURIComponent(String(opts.limit))}` : "";
  return apiGet<{
    account: { id: string; code: string; name: string; type: string };
    entries: Array<{
      lineId: string;
      journalId: string;
      journalNumber: string;
      kind: string;
      memo: string;
      sourceDocType: string | null;
      sourceDocId: string | null;
      /** Calendar date of effect (YYYY-MM-DD) */
      entryDate: string;
      /** When posted into books (ISO) — audit, not ledger Date */
      postedAt: string | null;
      debit: string;
      credit: string;
      balance: string;
    }>;
    totalDebit: string;
    totalCredit: string;
    balance: string;
    currencyCode: string;
  }>(`/c/${companyRef}/gl/accounts/${accountId}/ledger${q}`);
}

export function fetchJournal(companyRef: string, journalId: string) {
  return apiGet<{
    journal: {
      id: string;
      number: string;
      lifecycle: string;
      kind: string;
      memo: string | null;
      notes: string | null;
      sourceDocType: string | null;
      sourceDocId: string | null;
      entryDate: string;
      postedAt: string | null;
    };
    lines: Array<{
      id: string;
      accountId: string;
      accountCode: string;
      accountName: string;
      debit: string;
      credit: string;
      memo: string | null;
    }>;
    totalDebit: string;
    totalCredit: string;
    currencyCode: string;
  }>(`/c/${companyRef}/gl/journals/${journalId}`);
}

export function postJournal(
  companyRef: string,
  body: {
    kind?: "manual" | "opening";
    memo: string;
    /** Calendar date of effect YYYY-MM-DD */
    entryDate?: string;
    number?: string;
    idempotencyKey?: string;
    lines: Array<{
      accountCode: string;
      debit?: string;
      credit?: string;
    }>;
  },
) {
  return apiPost<{
    journal: { id: string; number: string };
    replayed?: boolean;
  }>(`/c/${companyRef}/gl/journals`, body);
}
